/**
 * Foreground-update flow.
 *
 * On every app foreground (and on initial mount in Capacitor), check the
 * OTA manifest. If a new bundle is available:
 *   - User is on `/` (home): apply automatically with a full-screen
 *     progress overlay. Reloading mid-session here is acceptable because
 *     home has no draft state to lose.
 *   - User is anywhere else: show a non-dismissible banner at the bottom.
 *     Tapping it applies the update + reloads.
 *
 * The "non-dismissible" choice is deliberate — the user said "users update
 * ASAP". Banner shows on every render until they tap it. If they ignore
 * indefinitely, the next foreground re-checks and re-banners.
 *
 * Apple 4.7 framing: the wording is "New version available" (routine
 * update), not "Required to continue" (forced replacement). Reviewers are
 * sensitive to that distinction.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download } from 'lucide-react';
import { isNative, onForegrounded } from '@ah/native/runtime';
import {
  checkForUpdate,
  downloadAndApplyUpdate,
  type UpdateAvailability,
} from '@ah/native/update';
import { env } from './env';

type ApplyState =
  | { kind: 'idle' }
  | { kind: 'downloading'; percent: number }
  | { kind: 'failed'; reason: string };

export function UpdateGate() {
  const [availability, setAvailability] = useState<UpdateAvailability | null>(null);
  const [applyState, setApplyState] = useState<ApplyState>({ kind: 'idle' });
  const location = useLocation();
  const isHome = location.pathname === '/';

  // Avoid stacking applies if the user double-taps or the auto-apply fires
  // while a manual apply is already in flight.
  const applyingRef = useRef(false);

  // Foreground listener — fires on every transition from background → active,
  // plus once at mount.
  useEffect(() => {
    if (!isNative()) {
      console.log('[UpdateGate] skipped: not native');
      return;
    }
    if (!env.VITE_OTA_MANIFEST_URL) {
      console.log('[UpdateGate] skipped: no manifest URL');
      return;
    }

    let cancelled = false;

    async function check(trigger: string) {
      if (cancelled) return;
      console.log(`[UpdateGate] check (${trigger}) — running version ${env.VITE_BUILD_VERSION}`);
      try {
        const result = await checkForUpdate(env.VITE_OTA_MANIFEST_URL, env.VITE_BUILD_VERSION);
        if (cancelled) return;
        console.log('[UpdateGate] result:', result);
        if (result.available) setAvailability(result);
      } catch (err) {
        console.error('[UpdateGate] check threw:', err);
      }
    }

    void check('mount');
    const off = onForegrounded(() => void check('foreground'));

    return () => {
      cancelled = true;
      off();
    };
  }, []);

  // Auto-apply when on home. The user-perceived latency is short enough
  // (2–4s on good network) that an unannounced reload is gentler than a
  // banner asking them to tap.
  useEffect(() => {
    if (!availability?.available) return;
    if (!isHome) return;
    if (applyingRef.current) return;
    void apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availability, isHome]);

  async function apply() {
    if (!availability?.available) return;
    if (applyingRef.current) return;
    applyingRef.current = true;
    setApplyState({ kind: 'downloading', percent: 0 });
    const result = await downloadAndApplyUpdate(availability, ({ percent }) => {
      setApplyState({ kind: 'downloading', percent });
    });
    if (!result.ok) {
      setApplyState({ kind: 'failed', reason: result.reason });
      applyingRef.current = false;
    }
    // On success the WebView reloads — nothing to set; the new bundle's
    // UpdateGate takes over after reload.
  }

  if (!availability?.available) return null;

  // Full-screen overlay — used on home AND when a banner-triggered apply is
  // mid-flight. Blocks interaction so the user can't navigate during the
  // reload window.
  if (applyState.kind === 'downloading') {
    return (
      <div
        className="fixed inset-0 z-[100] bg-background flex flex-col items-center justify-center gap-6 p-6"
        role="dialog"
        aria-modal="true"
        aria-label="Updating Underlings"
      >
        <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Download size={24} className="text-primary" />
        </div>
        <div className="text-center">
          <p className="text-base font-semibold">Updating Underlings…</p>
          <p className="text-sm text-muted-foreground mt-1">
            Should take a few seconds.
          </p>
        </div>
        <div className="w-full max-w-xs h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-primary transition-[width] duration-200 ease-out"
            style={{ width: `${Math.max(applyState.percent, 5)}%` }}
          />
        </div>
      </div>
    );
  }

  if (applyState.kind === 'failed') {
    // Surface the failure but stay non-blocking. The user can retry from
    // the banner; next foreground re-checks the manifest.
    return (
      <div
        className="fixed left-3 right-3 z-50 rounded-xl border border-destructive bg-background shadow-lg p-3 text-sm"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
      >
        <p className="font-semibold">Update failed</p>
        <p className="text-xs text-muted-foreground mt-0.5 mb-2">
          {applyState.reason.slice(0, 160)}
        </p>
        <button
          type="button"
          onClick={() => {
            setApplyState({ kind: 'idle' });
            void apply();
          }}
          className="px-3 h-8 rounded-full bg-primary text-primary-foreground text-xs font-medium"
        >
          Try again
        </button>
      </div>
    );
  }

  // Banner — non-home routes. Persists across navigation until the user
  // taps Update or the next foreground swaps in a different state.
  return (
    <div
      className="fixed left-3 right-3 z-40 rounded-xl border border-border bg-background shadow-lg p-3 flex items-center gap-3"
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
    >
      <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
        <Download size={18} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">New version available</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Tap to update — takes a few seconds.
        </p>
      </div>
      <button
        type="button"
        onClick={apply}
        className="shrink-0 px-4 h-9 rounded-full bg-primary text-primary-foreground text-sm font-medium"
      >
        Update
      </button>
    </div>
  );
}
